import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, deleteDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';

const AdminMigrationsPage = () => {
  const { currentUser } = useAuth();
  const [output, setOutput] = useState('Esperando acción...');
  const [isProcessing, setIsProcessing] = useState(false);

  const log = (message) => {
    setOutput((prev) => {
      const timestamp = new Date().toLocaleTimeString();
      return prev + `\n[${timestamp}] ${message}`;
    });
  };

  const clearOutput = () => {
    setOutput('');
  };

  // Función de normalización CORREGIDA
  const normalizeService = (oldService, docId) => {
    // Extraer precio de prices array si existe
    let finalPrice = 0;
    let pricesPackages = null;

    if (oldService.prices && Array.isArray(oldService.prices) && oldService.prices.length > 0) {
      pricesPackages = oldService.prices;
      finalPrice = oldService.prices[0].price || 0;
    } else if (oldService.price) {
      finalPrice = oldService.price;
    } else if (oldService.servicePrice) {
      finalPrice = oldService.servicePrice;
    }

    return {
      id: docId,
      userId: oldService.userId || oldService.userID || '',
      userName: oldService.userName || oldService.username || 'Usuario',
      userPhotoURL: oldService.userPhotoURL || null,
      caption: oldService.caption || oldService.title || '',
      description: oldService.description || '',
      category: oldService.category || 'general',
      categoryName: oldService.categoryName || oldService.category || '',
      subcategory: oldService.subcategory || null,
      tags: oldService.tags || [],
      media: oldService.imageUrls || oldService.media || [],
      imageUrls: oldService.imageUrls || [],
      imagePaths: oldService.imagePaths || [],
      servicePrice: parseFloat(finalPrice),
      prices: pricesPackages,
      currency: oldService.currency || 'ARS',
      deliveryTime: oldService.deliveryTime || null,
      specifications: oldService.specifications || null,
      terms: oldService.terms || null,
      allowClientDescription: oldService.allowClientDescription !== false,
      allowCustomizations: oldService.allowCustomizations !== false,
      status: oldService.status || 'active',
      workStatus: oldService.workStatus || 'open',
      isPublic: oldService.isPublic !== false,
      isActive: oldService.isActive !== false,
      isNSFW: oldService.isNSFW || false,
      likesCount: 0,
      commentsCount: 0,
      ordersCount: oldService.ordersCount || oldService.orders || 0,
      views: oldService.views || 0,
      createdAt: oldService.createdAt || null,
      updatedAt: oldService.updatedAt || null,
      customFields: oldService.customFields || {},
      _migrated: true,
      _migratedAt: new Date(),
      _originalCollection: 'Servicios'
    };
  };

  const handleRemigrate = async () => {
    if (!currentUser) {
      alert('Por favor, inicia sesión primero.');
      return;
    }

    if (!window.confirm('¿Eliminar los 57 servicios actuales y re-migrarlos con precios correctos?')) {
      return;
    }

    setIsProcessing(true);
    clearOutput();

    try {
      log('INICIANDO RE-MIGRACIÓN');
      log('═'.repeat(60));
      log(`Ejecutando como: ${currentUser.email}\n`);

      // PASO 1: Eliminar servicios actuales mal migrados
      log('PASO 1: Eliminando servicios mal migrados...');
      const servicesRef = collection(db, 'services');
      const servicesSnapshot = await getDocs(servicesRef);

      log(`Encontrados ${servicesSnapshot.size} servicios a eliminar`);

      let deleted = 0;
      for (const serviceDoc of servicesSnapshot.docs) {
        await deleteDoc(doc(db, 'services', serviceDoc.id));
        deleted++;
        if (deleted % 10 === 0) {
          log(`  Eliminados: ${deleted}/${servicesSnapshot.size}`);
        }
      }

      log(`✅ ${deleted} servicios eliminados\n`);

      // PASO 2: Obtener servicios originales
      log('PASO 2: Obteniendo servicios originales de "Servicios"...');
      const originalRef = collection(db, 'Servicios');
      const originalSnapshot = await getDocs(originalRef);

      log(`Encontrados ${originalSnapshot.size} servicios originales\n`);

      // PASO 3: Migrar con normalización corregida
      log('PASO 3: Re-migrando con precios correctos...');

      let migrated = 0;
      let withPrice = 0;
      let withPackages = 0;

      for (const originalDoc of originalSnapshot.docs) {
        const originalData = originalDoc.data();
        const normalized = normalizeService(originalData, originalDoc.id);

        await setDoc(doc(db, 'services', originalDoc.id), normalized);

        if (normalized.servicePrice > 0) withPrice++;
        if (normalized.prices && normalized.prices.length > 0) withPackages++;

        log(
          `✅ ${normalized.caption} - $${normalized.servicePrice} ${
            normalized.prices ? `(${normalized.prices.length} paquetes)` : ''
          }`
        );
        migrated++;
      }

      log('\n' + '═'.repeat(60));
      log('RESULTADO FINAL:');
      log(`  ✅ Servicios re-migrados: ${migrated}`);
      log(`  💰 Con precio > 0: ${withPrice}`);
      log(`  📦 Con paquetes: ${withPackages}`);
      log('\n¡RE-MIGRACIÓN COMPLETA!');
      log('Recarga tu perfil para ver los cambios.');
    } catch (error) {
      log(`❌ ERROR: ${error.message}`);
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-8 shadow-lg text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Acceso restringido</h1>
          <p className="text-gray-600 dark:text-gray-400">Debes iniciar sesión para acceder al panel de administración.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 mb-2">
              Panel de migraciones
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Herramientas de migración y mantenimiento</p>
            <div className="mt-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <p className="text-sm text-indigo-800 dark:text-indigo-200">
                <strong>Usuario:</strong> {currentUser.email}
              </p>
              <p className="text-xs text-indigo-600 dark:text-indigo-300 mt-1">
                <strong>UID:</strong> {currentUser.uid}
              </p>
            </div>
          </div>

          <div className="mb-6 p-5 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-lg">
            <h3 className="font-semibold text-amber-900 dark:text-amber-200 mb-1">IMPORTANTE</h3>
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Este script eliminará los servicios mal migrados y los volverá a migrar con los precios correctos.
            </p>
          </div>

          <div className="flex gap-4 mb-6">
            <button
              onClick={handleRemigrate}
              disabled={isProcessing}
              className="px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-semibold rounded-lg shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isProcessing ? 'Re-migrando...' : 'Re-migrar TODO (Eliminar + Migrar)'}
            </button>
            <button
              onClick={clearOutput}
              className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg shadow-lg transition-all"
            >
              Limpiar log
            </button>
          </div>

          <div className="bg-slate-900 text-green-400 p-6 rounded-xl font-mono text-sm leading-relaxed whitespace-pre-wrap overflow-y-auto max-h-[600px] shadow-inner">
            {output}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMigrationsPage;

